import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { canAccessBoringBucket } from '../../lib/entitlements';
import '../Dashboard.css';

const COMMUNITY_URL = 'https://community.theboringbucket.com';

// Two recurring Google Meet sessions, both 8-9pm ET. Same links every week.
const SESSIONS = [
  {
    day: 'Sunday',
    title: 'The Week Ahead',
    desc: 'Futures have just reopened — we look at the forward market and how the engines are set up for the week.',
    link: 'https://meet.google.com/zkm-bbyu-dza',
  },
  {
    day: 'Wednesday',
    title: 'Midweek Review',
    desc: 'How the week is playing out, open positions and trade discussion, member Q&A, and the new-member walkthrough.',
    link: 'https://meet.google.com/kkx-xmia-zeh',
  },
];

/**
 * Mentoring — schedule + recordings for the collective mentoring sessions.
 * Member-gated. Two weekly Google Meet sessions, both 8-9pm ET; recordings
 * live in the Discourse community.
 */
export const Mentoring: React.FC = () => {
  const { user } = useAuth();
  const isMember = canAccessBoringBucket(user as any);

  return (
    <div className="dashboard-container">
      <Navigation />
      <div className="dashboard-content">
        <div style={{ padding: '1.5rem' }}>
          <div className="welcome-section">
            <h2>Mentoring</h2>
            <p>Collective mentoring sessions — live, recorded, and open to the whole cohort.</p>
            <Link to="/boring-bucket" style={{ fontSize: 13, color: '#60a5fa' }}>← Boring Bucket</Link>
          </div>

          {!isMember ? (
            <div style={panel}>
              <h3 style={{ margin: '0 0 8px', color: '#f9fafb' }}>Members only</h3>
              <p style={{ color: '#94a3b8', fontSize: 14, margin: 0 }}>
                Mentoring is part of The Boring Bucket membership.{' '}
                <Link to="/boring-bucket" style={{ color: '#60a5fa' }}>Learn more →</Link>
              </p>
            </div>
          ) : (
            <>
              <div style={panel}>
                <h3 style={{ margin: '0 0 4px', color: '#f9fafb' }}>The weekly schedule</h3>
                <p style={{ color: '#94a3b8', fontSize: 14, margin: '0 0 8px' }}>
                  Two live sessions a week on Google Meet, both 8:00–9:00pm ET. The links
                  are the same every week.
                </p>
                {SESSIONS.map((s) => (
                  <div key={s.day} style={sessionRow}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: '#f9fafb', fontSize: 14, fontWeight: 600 }}>
                        {s.day} 8:00pm ET <span style={{ color: '#10b981' }}>· {s.title}</span>
                      </div>
                      <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 3 }}>{s.desc}</div>
                    </div>
                    <a href={s.link} target="_blank" rel="noopener noreferrer" style={joinBtn}>
                      Join
                    </a>
                  </div>
                ))}
                <p style={{ color: '#64748b', fontSize: 13, margin: '12px 0 0' }}>
                  New members: your capital-plan walkthrough and engine setup happen in the
                  first segment of the next Wednesday session.
                </p>
              </div>

              <div style={{ ...panel, marginTop: 14 }}>
                <h3 style={{ margin: '0 0 8px', color: '#f9fafb' }}>How mentoring works</h3>
                <ul style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.9, margin: 0, paddingLeft: 18 }}>
                  <li>Two sessions a week, both 8:00–9:00pm ET — collective, not 1:1.</li>
                  <li>Sunday sets up the week ahead; Wednesday reviews how it played out.</li>
                  <li>Every session is recorded and posted in the community.</li>
                  <li>Between sessions, ask in the community — it's answered there or in
                    the next session.</li>
                </ul>
              </div>

              <div style={{ ...panel, marginTop: 14 }}>
                <h3 style={{ margin: '0 0 8px', color: '#f9fafb' }}>Recordings</h3>
                <p style={{ color: '#94a3b8', fontSize: 13, margin: 0 }}>
                  Session recordings are posted in the community Mentoring area, usually
                  within a day.{' '}
                  <a href={COMMUNITY_URL} target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa' }}>
                    Open the community →
                  </a>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const panel: React.CSSProperties = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 10,
  padding: '20px 22px',
};

const sessionRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '12px 0',
  borderTop: '1px solid rgba(255,255,255,0.07)',
};

const joinBtn: React.CSSProperties = {
  flexShrink: 0,
  background: '#10b981',
  color: '#0a0f1e',
  fontWeight: 700,
  fontSize: 13,
  padding: '8px 20px',
  borderRadius: 8,
  textDecoration: 'none',
};

export default Mentoring;
